import React, { useCallback, useState } from 'react';

import { MessageDescriptor, useIntl } from 'react-intl-next';
import { useRelayEnvironment } from 'react-relay';

import Button from '@atlaskit/button/new';
import Modal, {
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Text, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';

import { connectTeamToContainer } from '../../../common/mutations/connect-team-to-container';
import { getTeamAri } from '../../../common/utils/ari';
import { TeamPicker } from '../team-picker';
import { TeamOptionData } from '../team-picker/main';

import messages from './messages';

const modalBodyStyles = xcss({
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

const DiscoveryMessage = ({
	formatMessage,
}: {
	formatMessage: (message: MessageDescriptor, values?: Record<string, string>) => string;
}) => {
	const [showDiscoveryMessage, setShowDiscoveryMessage] = useState(true);

	if (!showDiscoveryMessage) {
		return null;
	}

	return (
		<Box paddingBlockEnd="space.200">
			<SectionMessage
				title={formatMessage(messages.discoveryMessageTitle)}
				appearance="discovery"
				actions={[
					<SectionMessageAction onClick={() => setShowDiscoveryMessage(false)}>
						{formatMessage(messages.discoveryMessageAction)}
					</SectionMessageAction>,
					<SectionMessageAction href="#">
						{formatMessage(messages.discoveryMessageLearnMore)}
					</SectionMessageAction>,
				]}
			>
				<Text>{formatMessage(messages.discoveryMessage)}</Text>
			</SectionMessage>
		</Box>
	);
};

type AddTeamModalProps = {
	siteId: string;
	containerId: string;
	orgId: string;
	onClose: () => void;
};

export const AddTeamModal = ({ siteId, containerId, orgId, onClose }: AddTeamModalProps) => {
	const relayEnvironment = useRelayEnvironment();
	const { formatMessage } = useIntl();
	const [selectedTeam, setSelectedTeam] = useState<TeamOptionData | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const handleTeamSelection = useCallback(() => {
		if (!selectedTeam) {
			return;
		}

		const { id, name } = selectedTeam;
		const teamAri = getTeamAri(id);

		if (!teamAri) {
			return;
		}

		setIsLoading(true);

		connectTeamToContainer(
			relayEnvironment,
			{
				id: containerId,
				teamId: teamAri,
				name,
			},
			{
				onSuccess: () => {
					setIsLoading(false);
					onClose();
				},
				onLinkError: () => {
					setIsLoading(false);
					onClose();
				},
			},
		);
	}, [containerId, relayEnvironment, selectedTeam, onClose]);

	return (
		<ModalTransition>
			<Modal width="small" onClose={onClose}>
				<ModalHeader>
					<ModalTitle>{formatMessage(messages.heading)}</ModalTitle>
				</ModalHeader>
				<Box xcss={modalBodyStyles}>
					<DiscoveryMessage formatMessage={formatMessage} />
					<TeamPicker
						siteId={siteId}
						orgId={orgId}
						onTeamSelection={(team) => setSelectedTeam(team)}
						onClear={() => setSelectedTeam(null)}
					/>
				</Box>
				<ModalFooter>
					<Button appearance="subtle" onClick={onClose}>
						{formatMessage(messages.cancelButton)}
					</Button>
					<Button
						appearance="primary"
						isDisabled={!selectedTeam}
						isLoading={isLoading}
						onClick={handleTeamSelection}
					>
						{formatMessage(messages.addButton)}
					</Button>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
